import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  categories: [],
  loading: false,
  error: null,
};

// Async Thunk: Fetch All Categories
export const fetchAllCategories = createAsyncThunk(
  'category/fetchAllCategories',
  async (options = {}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/categories');
      if (res.data.success) {
        return res.data.categories;
      }
      return rejectWithValue('Failed to fetch categories');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (options = {}, { getState }) => {
      if (options.force) return true;
      const { category } = getState();
      if (category.loading || (category.categories && category.categories.length > 0)) {
        return false;
      }
    }
  }
);

// Async Thunk: Create Category
export const createCategory = createAsyncThunk(
  'category/createCategory',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/categories', formData);
      if (res.data.success) {
        return res.data.category;
      }
      return rejectWithValue('Failed to create category');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Update Category
export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/categories/${id}`, formData);
      if (res.data.success) {
        return res.data.category;
      }
      return rejectWithValue('Failed to update category');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Delete Category
export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/categories/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to delete category');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Reorder Categories
export const reorderCategories = createAsyncThunk(
  'category/reorderCategories',
  async (orders, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/categories/reorder', { orders });
      if (res.data.success) {
        return orders;
      }
      return rejectWithValue('Failed to reorder categories');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchAllCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.unshift({ gameCount: 0, ...action.payload });
        state.loading = false;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.categories[index] = {
            ...state.categories[index],
            ...action.payload,
            gameCount: action.payload.gameCount ?? state.categories[index].gameCount ?? 0,
          };
        }
        state.loading = false;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(c => c._id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reorder Categories
      .addCase(reorderCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(reorderCategories.fulfilled, (state, action) => {
        state.loading = false;
        const orders = action.payload;
        state.categories = state.categories.map(cat => {
          const match = orders.find(o => o.id === (cat._id || cat.id));
          if (match) {
            return { ...cat, sortOrder: match.sortOrder };
          }
          return cat;
        }).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      })
      .addCase(reorderCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default categorySlice.reducer;
